import { siteApiSlice } from "../../app/siteApiSlice";
import { UserRole } from "../../app/userSlice";
import { DataTablesViewModel, DataTablesQueryModel } from "../../interfaces/dataTables";

export interface TutorialViewModel {
	tutorialId: number;
	name: string;
	shortName: string;
	previewImage: string;
	location: string;
	categoryId: number;
	level: string;
	difficulty: string;
	durationSeconds: number;
	description: string;
	audio: boolean;
	codeName: string;
}

export interface TutorialCategoryViewModel {
	categoryId: number;
	name: string;
	order: number;
}

export interface TutorialSessionViewModel {
	sessionId: number;
	userId: number;
	courseId: number;
	tutorialId: number;
	timeStarted: string;
	timeSpent: string;
	timeSpentSeconds: number;
}

export interface TutorialAssignmentViewModel {
	assignmentId: number;
	createDate: string;
	createUserId: number;
	disabled: boolean;
	sessionId: number;
	tutorialId: number;
	timeStarted: string;
	timeSpent: string;
	timeSpentSeconds: number;
}

export interface TutorialCourseActivityViewModel {
	assignedLessons: TutorialCourseAssignmentViewModel[];
}

export interface TutorialCourseAssignmentViewModel {
	tutorialId: number;
	studentAssignments: TutorialStudentAssignmentViewModel[];
	startedCount: number;
	incompleteCount: number;
	totalCount: number;
	completed: boolean;
}

export interface TutorialStudentAssignmentViewModel {
	userId: number;
	formalName: string;
	initials: string;
	hasImage: boolean;
	timeSpentSeconds: number;
	timeStarted: string;
	completed: boolean;
}

export interface TutorialActivityViewModel {
	sessions: TutorialSessionViewModel[];
	assignments: TutorialAssignmentViewModel[];
	recommendations: number[];
}

export interface PlayTutorialViewModel {
	id: number;
	categoryId: number;
	tutorialName: string;
	categoryName: string;
	path: string;
	role: UserRole;
}

export interface SelectDataViewModel {
	dT_RowId: string;
	name: string;
	username: string;
	level: string;
}

export interface SelectQueryModel extends DataTablesQueryModel {
	level_Filter: string;
	search_Filter: string;
}

export interface AssignLessonViewModel {
	tutorialIds: number[];
	studentIds: number[];
}

export interface RemoveViewModel {
	tutorialId: number;
	studentIds?: number[];
}

export const lessonsApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		getTutorials: build.query<Array<TutorialViewModel>, void>({
			query: () => ({
				url: `/Tutorial/GetTutorials`,
			}),
			keepUnusedDataFor: 60 * 60 * 4,
		}),
		getCategories: build.query<Array<TutorialCategoryViewModel>, void>({
			query: () => ({
				url: `/Tutorial/GetCategories`,
			}),
			keepUnusedDataFor: 60 * 60 * 4,
		}),
		getUserActivity: build.query<TutorialActivityViewModel, void>({
			query: () => ({
				url: `/Tutorial/GetUserActivity`,
			}),
			providesTags: ["LessonHistory", "CurrentCourse"],
		}),
		getCourseActivity: build.query<TutorialCourseActivityViewModel, void>({
			query: () => ({
				url: `/Tutorial/GetCourseActivity`,
			}),
			providesTags: ["LessonHistory", "CurrentCourse"],
		}),
		playTutorial: build.query<PlayTutorialViewModel, string>({
			query: (codeName) => ({
				url: `/Lessons/${codeName}/Play`,
				method: "GET",
			}),
			async onQueryStarted(codeName, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(siteApiSlice.util.invalidateTags(["Notifications", "LessonHistory"]));
				} catch (err) {}
			},
		}),
		updateSessionTime: build.mutation<void, number>({
			query: (tutorialId) => ({
				url: `/Tutorial/UpdateSessionTimeSpent`,
				method: "POST",
				body: { id: tutorialId },
			}),
			invalidatesTags: ["LessonHistory"],
		}),
		getAllTutorials: build.query<DataTablesViewModel<SelectDataViewModel>, SelectQueryModel>({
			query: (q) => {
				const {
					iSortCol_0,
					sSortDir_0,
					iDisplayStart,
					iDisplayLength,
					level_Filter,
					search_Filter,
				} = q;
				return {
					url: `/TutorialWriters/SelectTutorialsAjaxHandler`,
					params: {
						iSortCol_0,
						sSortDir_0,
						iDisplayStart,
						iDisplayLength,
						level_Filter,
						search_Filter,
					},
				};
			},
			providesTags: ["LessonHistory", "CurrentCourse"],
		}),
		getAllStudents: build.query<
			DataTablesViewModel<SelectDataViewModel>,
			{ model: SelectQueryModel; tutorialId?: number }
		>({
			query: (q) => {
				const {
					iSortCol_0,
					sSortDir_0,
					iDisplayStart,
					iDisplayLength,
					level_Filter,
					search_Filter,
				} = q.model;
				const tutorialId = q.tutorialId;
				return {
					url: `/TutorialWriters/SelectStudentsAjaxHandler`,
					params: {
						iSortCol_0,
						sSortDir_0,
						iDisplayStart,
						iDisplayLength,
						level_Filter,
						search_Filter,
						tutorialId,
					},
				};
			},
			providesTags: ["LessonHistory", "CurrentCourse"],
		}),
		assignLesson: build.mutation<void, AssignLessonViewModel>({
			query: (q) => {
				const { tutorialIds, studentIds } = q;
				return {
					url: `/TutorialWriters/AssignTutorial`,
					method: "POST",
					body: { tutorialIds, studentIds },
				};
			},
			invalidatesTags: ["LessonHistory"],
		}),
		removeLessonAssignment: build.mutation<string, RemoveViewModel>({
			query: (q) => {
				const { tutorialId, studentIds } = q;
				return {
					url: `/TutorialWriters/Remove`,
					method: "POST",
					body: { tutorialId, studentIds },
				};
			},
			invalidatesTags: ["LessonHistory"],
		}),
		removeAllLessonAssignments: build.mutation<string[], number[]>({
			query: (tutorialIds) => ({
				url: `/TutorialWriters/RemoveAll`,
				method: "POST",
				body: { tutorialIds },
			}),
			invalidatesTags: ["LessonHistory"],
		}),
	}),
});

export const {
	useGetTutorialsQuery,
	useGetCategoriesQuery,
	useGetUserActivityQuery,
	useGetCourseActivityQuery,
	usePlayTutorialQuery,
	useUpdateSessionTimeMutation,
	useLazyGetAllTutorialsQuery,
	useLazyGetAllStudentsQuery,
	useAssignLessonMutation,
	useRemoveLessonAssignmentMutation,
	useRemoveAllLessonAssignmentsMutation,
} = lessonsApiSlice;
